import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { RegisterService } from '../../../../services/register/register.service';
import { I18nHelperService, TranslateService } from '@xref/ngx';
import { forkJoin } from 'rxjs';
import cloneDeep from 'lodash.clonedeep';

const find = require('lodash.find');

@Component({
    selector: 'xlog-reg-setup',
    templateUrl: './set-up.component.pug',
    styleUrls: ['./set-up.component.scss'],
    standalone: false
})
export class RegSetUpComponent {
    @Input() setUpForm: UntypedFormGroup;
    @Input() submitting = false;

    @Output() setupFormSubmitted = new EventEmitter();

    unalteredCountryList: string[] = [];
    countryList: string[] = [];

    mostUsedCountries: string[] = [];

    constructor(
        public reg: RegisterService,
        private i18nHelper: I18nHelperService,
        private translate: TranslateService
    ) {
        this.setCountryList();

        this.i18nHelper.localeChanged$.subscribe(() => this.translateCountries());
    }

    get firstName() { return this.setUpForm.get('firstName'); }
    get lastName() { return this.setUpForm.get('lastName'); }
    get email() { return this.setUpForm.get('email'); }
    get country() { return this.setUpForm.get('country'); }
    get phoneCountry() { return this.setUpForm.get('phoneCountry'); }
    get phoneNumber() { return this.setUpForm.get('phoneNumber'); }

    setCountryList() {
        this.reg.getCountryDetails().subscribe(
            (res: any) => {
                this.countryList = res;
                this.unalteredCountryList = cloneDeep(this.countryList);
                this.setCountryByIP();

                this.translateCountries();
            }
        );
    }

    setCountryByIP() {
        try {
            window['geoip2'].country((res) => {
                const resolvedCountryName = res.country.names.en;
                const countryInstance = find(this.countryList, {translationKey: resolvedCountryName});

                // once ip is resolved, update country and phone should they are not set.
                if (!this.setUpForm.controls.country.value) {
                    this.setUpForm.controls.country.setValue(countryInstance.id);
                    this.setUpForm.controls.phoneCountry.setValue(countryInstance.phone_code);
                    this.setUpForm.controls.phoneCountry.disable();
                }
            });
        } catch (error) {
            console.warn('geo ip library could not be resolved.', error);
        }

    }

    emailChanged() {
        this.setUpForm.controls.overrideDomainWarning.setValue(false);
    }

    countryChanged(countryId) {
        const selectedCountry = parseInt(countryId, 10);
        const phoneCode = find(this.countryList, {id: selectedCountry}).phone_code;
        this.setUpForm.controls.phoneCountry.setValue(phoneCode);
    }

    hasNonCorpEmailErr() {
        return (
            this.reg.setUpFormErrors.ERROR_DOMAIN_INVALID ||
      this.reg.setUpFormErrors.ERROR_EMAIL_INVALID ||
      this.reg.setUpFormErrors.ERROR_NO_PLUS_CHAR
        );
    }

    submit() {
    // set region based on selected country
        const selectedCountry = parseInt(this.setUpForm.controls.country.value, 10);
        const region = find(this.countryList, {id: selectedCountry}).region.toLowerCase();
        this.setUpForm.controls.dataRegion.setValue(region);

        // Override domain warning before submit
        if (this.reg.setUpFormErrors.WARNING_DOMAIN_IS_ENTERPRISE)
            this.setUpForm.controls.overrideDomainWarning.setValue(true);

        this.setupFormSubmitted.emit();
    }

    private translateCountries() {
        this.countryList = cloneDeep(this.unalteredCountryList);
        try {
            const countryList = this.countryList.map((country) => {
                country['translationKey'] = country['country'];
                return country;
            });
            const translateObservables = [];

            for (let i = 0; i < countryList.length; i++)
                translateObservables.push(this.translate.get('countries.' + countryList[i]['country']));

            forkJoin(translateObservables).subscribe({
                next: (value: string[]) => {
                    for (let i = 0; i < countryList.length; i++) {
                        countryList[i]['country'] = value[i];
                    }
                },
                complete: () => {
                    // Sort list based on translated name
                    countryList.sort(function (a, b) {
                        return new Intl.Collator().compare(a['country'], b['country']);
                    });

                    this.countryList = countryList;
                    this.getMostUsedCountries(['Australia', 'Canada', 'United Kingdom', 'United States']);
                }
            });
        } catch (error) {
            console.warn('Error generating i18n strings for the selected locale.');
        }

    }

    private getMostUsedCountries(mostUsedNames: string[] = []) {
        const reducer = (acc: any[], curr: any) => (mostUsedNames.includes(curr.translationKey) ? [...acc, curr] : acc);
        this.mostUsedCountries = this.countryList.reduce(reducer, []);
    }
}
